/**
 * 腾讯广告命名模板页：「策略配置 × 二级策略」合法映射编辑器（腾讯域专用，单例配置）。
 *
 * 数据源：hooks/useStraSecondMapping（映射记录 + 命名模板枚举选项）。
 *   - 策略配置：下拉单选，选项来自命名模板「策略配置」字段枚举，行间不可重复；
 *   - 二级策略：下拉多选，选项来自命名模板「二级策略」字段枚举（已过滤 *MTCL 等后缀项）。
 * 保存：PUT /api/v1/tencent/stra-second-mapping（覆盖式单例保存），
 *       保存后红线 check_ad_name() 即按新映射校验（统一大写、剔除 *MTCL 后缀）。
 *
 * UI 规范：已读 docs/frontend_ui_guidline/leo-frontend-design.md v1.0，
 * 适用 §2 设计 Token / §3.2 字号阶梯 / §6.3 表单控件 / §6.4 卡片。
 */

import { useEffect, useMemo, useState } from "react";
import { ApiError } from "../../services/apiClient";
import { saveMapping } from "../../services/tencent/straSecondMapping";
import { useStraSecondMapping } from "../../hooks/useStraSecondMapping";
import { pushToast } from "../../hooks/useToast";
import type { StraSecondMappingRecord } from "../../types/straSecondMapping.types";

interface MappingRow {
  stra: string;
  seconds: string[];
}

interface Props {
  /** 只读模式（当前用户无该页编辑权限时置 true）：禁用全部编辑与保存 */
  readOnly?: boolean;
}

export function StraSecondMappingEditor({ readOnly = false }: Props) {
  const { loading, record, straOptions, secondOptions, reload } =
    useStraSecondMapping();
  const [rows, setRows] = useState<MappingRow[]>([]);
  const [saving, setSaving] = useState(false);

  // 映射记录变化（首次加载 / 保存后 reload）时重置编辑态
  useEffect(() => {
    setRows(recordToRows(record));
  }, [record]);

  const dirty = useMemo(
    () => JSON.stringify(rows) !== JSON.stringify(recordToRows(record)),
    [rows, record],
  );

  const optionsMissing = straOptions.length === 0 || secondOptions.length === 0;

  function updateRow(idx: number, next: Partial<MappingRow>) {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...next } : r)));
  }

  function deleteRow(idx: number) {
    setRows((rs) => rs.filter((_, i) => i !== idx));
  }

  function addRow() {
    setRows((rs) => [...rs, { stra: "", seconds: [] }]);
  }

  async function onSave() {
    for (const r of rows) {
      if (!r.stra) {
        pushToast("存在未选择策略配置的行", "error");
        return;
      }
      if (r.seconds.length === 0) {
        pushToast(`策略配置「${r.stra}」未选择二级策略`, "error");
        return;
      }
    }
    const mapping: Record<string, string[]> = {};
    for (const r of rows) mapping[r.stra] = r.seconds;

    setSaving(true);
    try {
      await saveMapping({ mapping });
      pushToast("映射保存成功", "success");
      await reload();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `${err.message}${err.suggestion ? `（${err.suggestion}）` : ""}`
          : "保存失败";
      pushToast(msg, "error");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="leo-card rounded-xl p-5 text-[12.5px] text-text-muted">
        映射配置加载中…
      </div>
    );
  }

  return (
    <div className="leo-card rounded-xl p-5">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <div className="text-[13px] font-semibold text-text-emphasis">
          策略配置 × 二级策略 合法映射
        </div>
        {record?.updated_time ? (
          <span className="text-[11px] text-text-muted-weak">
            最近更新：{record.updated_time.replace("T", " ")}
          </span>
        ) : null}
      </div>
      <p className="mb-4 text-[11.5px] leading-relaxed text-text-muted">
        红线校验规则：广告命名第 4 位（策略配置）与第 12 位（二级策略，比对前剔除
        *MTCL 后缀）必须命中下表映射，大小写不敏感；未命中即触发命名预警。
        表中未列出的策略配置不参与映射校验。
      </p>

      {optionsMissing ? (
        <div className="mb-4 rounded-md border border-semantic-warning/30 bg-semantic-warning/10 px-3 py-2 text-[12px] text-semantic-warning">
          未在上方命名模板中检测到「策略配置」或「二级策略」字段的枚举配置，请先在模板中配置后再维护映射。
        </div>
      ) : null}

      {/* 表头 */}
      <div className="mb-1.5 flex items-center gap-3 border-b border-line-subtle pb-2">
        <div className="w-[160px] shrink-0 text-[11px] font-semibold uppercase tracking-[0.06em] text-text-muted">
          策略配置
        </div>
        <div className="flex-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-text-muted">
          二级策略（合法值，可多选）
        </div>
        <div className="w-[30px] shrink-0" />
      </div>

      <div className="space-y-2">
        {rows.map((row, idx) => (
          <div key={idx} className="flex items-center gap-3">
            {/* 宽度约束放在外层 div：.leo-select 自带 width:100%（index.css 非 layer
                定义，优先级高于 Tailwind 工具类），直接写在 select 上的 w-[160px] 无效 */}
            <div className="w-[160px] shrink-0">
              <select
                className="leo-select"
                value={row.stra}
                disabled={readOnly}
                onChange={(e) => updateRow(idx, { stra: e.target.value })}
              >
                <option value="">（请选择）</option>
                {straOptions
                  .filter(
                    (code) =>
                      code === row.stra ||
                      !rows.some((r, i) => i !== idx && r.stra === code),
                  )
                  .map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
              </select>
            </div>
            <div className="flex-1">
              <SecondStraMultiSelect
                options={secondOptions}
                selected={row.seconds}
                disabled={readOnly}
                onChange={(next) => updateRow(idx, { seconds: next })}
              />
            </div>
            <button
              type="button"
              onClick={() => deleteRow(idx)}
              disabled={readOnly}
              className="leo-btn-ghost is-danger w-[30px] shrink-0 px-2 py-1.5 text-[12px]"
              title="删除该行"
            >
              ✕
            </button>
          </div>
        ))}
        {rows.length === 0 ? (
          <div className="py-3 text-center text-[12px] text-text-muted-weak">
            尚未配置映射，请点击「+ 新增映射」
          </div>
        ) : null}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <button
          type="button"
          onClick={addRow}
          disabled={readOnly || optionsMissing}
          className="leo-btn-ghost"
        >
          + 新增映射
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setRows(recordToRows(record))}
            disabled={!dirty || saving || readOnly}
            className="leo-btn-ghost"
          >
            重置
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving || !dirty || readOnly || optionsMissing || rows.length === 0}
            className="leo-cta-primary w-auto px-7"
          >
            {saving ? "保存中..." : dirty ? "保存映射" : "已保存"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** 映射 Record → 编辑行（保持键顺序）。 */
function recordToRows(
  record: StraSecondMappingRecord | null,
): MappingRow[] {
  return Object.entries(record?.mapping ?? {}).map(([stra, seconds]) => ({
    stra,
    seconds: [...seconds],
  }));
}

/** 二级策略下拉多选：触发器内以芯片展示已选，点击展开选项面板切换。 */
function SecondStraMultiSelect({
  options,
  selected,
  onChange,
  disabled = false,
}: {
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  function toggle(code: string) {
    onChange(
      selected.includes(code)
        ? selected.filter((c) => c !== code)
        : [...selected, code],
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="leo-input flex min-h-[36px] w-full flex-wrap items-center gap-1 text-left"
      >
        {selected.length === 0 ? (
          <span className="text-text-muted-weak">请选择二级策略（可多选）</span>
        ) : (
          selected.map((code) => (
            <span
              key={code}
              className="rounded-md bg-da-inset px-1.5 py-0.5 font-mono text-[11px] text-text-strong"
            >
              {code}
            </span>
          ))
        )}
      </button>
      {open && !disabled ? (
        <>
          {/* 透明遮罩：点击面板外区域收起 */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-line-subtle bg-da-card p-1.5 shadow-card">
            {options.length === 0 ? (
              <div className="px-2 py-1.5 text-[12px] text-text-muted-weak">
                （无可选项）
              </div>
            ) : (
              options.map((code) => {
                const on = selected.includes(code);
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => toggle(code)}
                    className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] hover:bg-da-inset ${
                      on ? "text-accent-cta-hover" : "text-text-body"
                    }`}
                  >
                    <span
                      className={`grid h-3.5 w-3.5 shrink-0 place-items-center rounded-sm border text-[10px] ${
                        on
                          ? "border-accent-cta bg-accent-cta/20"
                          : "border-line-strong"
                      }`}
                    >
                      {on ? "✓" : ""}
                    </span>
                    <span className="font-mono">{code}</span>
                  </button>
                );
              })
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
